import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import { StyleRulesCallback, withStyles } from '@material-ui/core/styles';
import { createForm } from 'rc-form';
// annotated imports are used in the part of the code that has been temporarily disabled
import {
    Typography,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    // Chip,
    Grid,
} from '@material-ui/core';
import CancelPresentation from '@material-ui/icons/CancelPresentation';

import { fetchUsersAccess } from 'Console/data/applicationDetail/settings/SettingsAction';

import {
    selectUsersAccessData,
    selectUsersAccessIsLoading,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import FormSelect from 'Console/components/common/form/FormSelect';
// import FormItemSelect from 'Console/components/common/form/FormItemSelect';
// import FormCheckbox from 'Console/components/common/form/FormCheckbox';

import { pushNotificationToStack } from 'Console/data/notification/actions';
// import { USER_ACCESS_RIGHTS } from 'Console/constants/appConstants';

const styles: StyleRulesCallback = (theme) => ({
    title: {
        display: 'inline-block',
        'font-weight': 'bold',
        'margin-bottom': theme.typography.pxToRem(30),
        'margin-right': theme.typography.pxToRem(61),
    },
    items: {
        [theme.breakpoints.between('xs', 'sm')]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    listItems: {
        width: '100%',
        paddingTop: theme.typography.pxToRem(20),
    },
    listItem: {
        padding: 0,
    },
    inputWidth: {
        paddingRight: theme.typography.pxToRem(21),
        width: '37%',
        [theme.breakpoints.between('xs', 'sm')]: {
            paddingRight: 0,
            overflow: 'hidden',
        },
    },
    chipsBox: {
        display: 'flex',
        width: '37%',
        paddingLeft: theme.typography.pxToRem(20),
        [theme.breakpoints.between('xs', 'sm')]: {
            width: 'auto',
            paddingLeft: 0,
            marginRight: 0,
        },
    },
    chip: {
        marginRight: theme.typography.pxToRem(10),
    },
    formSelect: {
        width: '37%',
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
            marginBottom: theme.typography.pxToRem(30),
        },
    },
    formItemSelect: {
        width: '37%',
        paddingRight: theme.typography.pxToRem(21),
        paddingLeft: theme.typography.pxToRem(18),
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
            marginBottom: theme.typography.pxToRem(30),
            paddingLeft: 0,
            marginLeft: 0,
        },
    },
    preset: {
        width: '12%',
        textAlign: 'center',
        [theme.breakpoints.between('xs', 'sm')]: {
            width: 'auto',
            textAlign: 'left',
            marginLeft: 0,
        },
    },
});

const AccessSettingsForm = ({
    classes,
    intl,
    form,
    initialValue,
    form: { getFieldsValue, getFieldDecorator, getFieldValue, setFieldsValue },
    // presets = true,
    className,
    guide,
    onChange,
}): React.ReactElement => {
    const dispatch = useDispatch();

    const userList = useSelector(selectUsersAccessData);
    const usersIsLoading = useSelector(selectUsersAccessIsLoading);

    useEffect(() => {
        dispatch(
            fetchUsersAccess({
                fail: (
                    <FormattedMessage
                        id="Notification.fail.loadData"
                        defaultMessage="Unable to load data, something went wrong"
                    />
                ),
            })
        );
    }, [dispatch]);

    const handleAddRow = useCallback(() => {
        const { newUserName, usersPermissions } = getFieldsValue([
            'newUserName',
            'usersPermissions',
        ]);

        setFieldsValue({
            usersPermissions: {
                read: usersPermissions.read,
                write: usersPermissions.write,
                admin: [...usersPermissions.admin, newUserName.name],
            },
            newUserName: '',
        });
        // when we will be use except admin too writer and reader,

        // switch (newUserPermissions) {
        //     case 'admin':
        //         setFieldsValue({
        //             usersPermissions: {
        //                 read: usersPermissions.read,
        //                 write: usersPermissions.write,
        //                 admin: [...usersPermissions.admin, newUserName.name],
        //             },
        //             newUserName: '',
        //         });

        //         break;
        //     case 'write':
        //         setFieldsValue({
        //             usersPermissions: {
        //                 admin: usersPermissions.admin,
        //                 write: [...usersPermissions.write, newUserName.name],
        //                 read: usersPermissions.read,
        //             },
        //             newUserName: '',
        //         });

        //         break;
        //     case 'read':
        //         setFieldsValue({
        //             usersPermissions: {
        //                 admin: usersPermissions.admin,
        //                 write: usersPermissions.write,
        //                 read: [...usersPermissions.read, newUserName.name],
        //             },
        //             newUserName: '',
        //         });

        //         break;
        //     default:
        //         return;
        // }

        onChange(getFieldValue('usersPermissions'));
    }, [getFieldsValue, getFieldValue, setFieldsValue, onChange]);

    const handleRemoveItem = useCallback(
        (removeUser) => {
            const usersPermissions = getFieldValue('usersPermissions');

            if (initialValue.owner.id !== removeUser) {
                setFieldsValue({
                    usersPermissions: {
                        read: usersPermissions.read.filter((item) => item !== removeUser),
                        write: usersPermissions.write.filter((item) => item !== removeUser),
                        admin: usersPermissions.admin.filter((item) => item !== removeUser),
                    },
                });

                onChange(getFieldValue('usersPermissions'));
            } else {
                dispatch(
                    pushNotificationToStack(
                        <FormattedMessage
                            id="Notification.remove.user"
                            defaultMessage="This user is the owner of this space, therefore cannot be removed"
                        />
                    )
                );
            }
        },
        [dispatch, initialValue.owner.id, getFieldValue, setFieldsValue, onChange]
    );

    const { admin, read, write } = initialValue.acl;

    getFieldDecorator('usersPermissions', {
        initialValue: {
            admin,
            read,
            write,
        },
        preserve: true,
    });

    const { newUserName, usersPermissions } = getFieldsValue([
        'newUserName',
        'usersPermissions',
    ]);

    const usersWithPermissions: string[] = [];

    Object.keys(usersPermissions).forEach((permissionGroup) => {
        usersWithPermissions.push(...usersPermissions[permissionGroup]);
    });
    const filterUserList = userList.filter(
        (user) => !usersWithPermissions.find((searchedUser) => searchedUser === user.username)
    );

    return (
        <Grid container className={`${classes.root} ${className}`}>
            <Grid item xs={12}>
                <Grid container>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="Settings.access.owner" defaultMessage="Owner" />
                    </Typography>

                    {guide ? (
                        <Typography variant="subtitle1">{guide.owner}</Typography>
                    ) : (
                        <Typography variant="subtitle1">{initialValue.owner.id}</Typography>
                    )}
                </Grid>
                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="Settings.access.users" defaultMessage="Users" />
                    </Typography>
                </Grid>
                <Grid container direction="row" alignItems="center" className={classes.items}>
                    {!usersIsLoading && userList && (
                        <Grid className={classes.formSelect}>
                            <FormSelect
                                form={form}
                                fullWidth={false}
                                isRequired
                                formItemKey="newUserName"
                                placeholder={intl.formatMessage({ id: 'Settings.access.label' })}
                                emptyListMessage={intl.formatMessage({ id: 'Settings.access.emptyList' })}
                                optionList={filterUserList.map((user) => ({
                                    id: user.id,
                                    name: user.username,
                                }))}
                                optionListIsLoading={usersIsLoading}
                            />
                        </Grid>
                    )}

                    {/* {presets && (
                            <Typography className={classes.preset}>
                                <FormattedMessage
                                    id="Settings.access.presets"
                                    defaultMessage="Presets"
                                />
                            </Typography>
                        )} */}

                    {/* <Grid className={classes.formItemSelect}>
                            <FormItemSelect
                                initialValue="read"
                                form={form}
                                fullWidth={false}
                                formItemKey="newUserPermissions"
                                optionList={USER_ACCESS_RIGHTS}
                            />
                        </Grid> */}

                    <Typography>
                        <Button
                            id="yeself-button"
                            style={{ marginLeft: 10 }}
                            variant="contained"
                            color="primary"
                            onClick={handleAddRow}
                            disabled={!newUserName}
                        >
                            <FormattedMessage id="Buttons.add" defaultMessage="Add" />
                        </Button>
                    </Typography>
                </Grid>

                <Grid container>
                    <List className={classes.listItems}>
                        {Object.keys(usersPermissions).map((permissionGroup) =>
                            usersPermissions[permissionGroup].map((user, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <ListItem key={index} className={classes.listItem}>
                                    <Grid container alignItems="center">
                                        <Grid item className={classes.inputWidth}>
                                            <ListItemText primary={user} />
                                        </Grid>

                                        {/* {presets && (
                                                <FormCheckbox
                                                    initialValue={false}
                                                    form={form}
                                                    formItemKey={`preset-${index}`}
                                                    className={classes.preset}
                                                />
                                            )}

                                            <div className={classes.chipsBox}>
                                                {USER_ACCESS_RIGHTS.map((permission) => (
                                                    <Grid item key={permission.value}>
                                                        <Chip
                                                            color="secondary"
                                                            label={permission.name}
                                                            value={permission.value}
                                                            variant={
                                                                permission.value === permissionGroup
                                                                    ? 'default'
                                                                    : 'outlined'
                                                            }
                                                            className={classes.chip}
                                                        />
                                                    </Grid>
                                                ))}
                                            </div> */}

                                        <Grid item className={classes.deleteBtn}>
                                            <ListItemIcon>
                                                <IconButton
                                                    aria-label="Delete"
                                                    onClick={() => handleRemoveItem(user)}
                                                >
                                                    <CancelPresentation />
                                                </IconButton>
                                            </ListItemIcon>
                                        </Grid>
                                    </Grid>
                                </ListItem>
                            ))
                        )}
                    </List>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(createForm()(injectIntl(AccessSettingsForm)));
